import { useLevels } from "./levels";
import { BookOpen, Layers, Settings } from "lucide-react";
import { helpUrl } from "./help";
import { useTranslation } from "react-i18next";
import { NavLink, Outlet, useNavigate, useSearchParams } from "react-router";
import { clsx } from "clsx";
import { Pill } from "../ui";
import { useSyncOutage, useSyncStatus } from "../sync/useSyncStatus";
import { useSession } from "./session";
import { TrackingStatus } from "../tracking/TrackingStatus";
import { useTrackingReconcile } from "../tracking/useTracking";
import { useCoreDoc } from "./useCoreDoc";
import { SearchBox } from "../search/SearchBox";
import { DashboardsMenu } from "./DashboardsMenu";
import { StarterAlarm } from "./StarterAlarm";
import { Background } from "./Background";
import { TrayStrip } from "./TrayStrip";
import { WorkspaceSwitch } from "./WorkspaceSwitch";
import { deviceAction, giveBack, lendDevice, useIdleReturn } from "../circle/device";
import { useCircle } from "../circle/useCircle";
import { setTrackingScope } from "../tracking/timelog";
import { atLeast } from "./settings";
import { Toasts } from "../ui/Toasts";
import { TaskPanel } from "../tasks/TaskPanel";
import { useViewCtx } from "./useViewCtx";
import { useSettings } from "./settings";
import { useApplySettings } from "./useApplySettings";
import { useDocState } from "../core/useDocSelector";
import { redo, undo } from "../sync/undo";
import { useCallback, useEffect, useState, type ReactNode } from "react";
import { useSignalsLoader } from "./signals";
import { AssistantPanel } from "../assistant/AssistantPanel";
import { sideBySide, useLayout } from "./layout";
import { MoreMenu } from "./MoreMenu";
import { TabBar } from "./TabBar";
import { PanelFrame } from "./PanelFrame";

const navLink =
  "rounded-sm px-2.5 py-1.5 text-[13.5px] text-neutral-500 transition hover:bg-neutral-100 hover:text-neutral-900 dark:hover:bg-neutral-800 dark:hover:text-neutral-100";

/** The framed look of the top bar's controls (space, energy): buttons there match it (FRONTEND_DESIGN.md). */
const barButton =
  "inline-flex h-[30px] items-center gap-1 rounded-sm border border-neutral-200 bg-[var(--surface)] px-2.5 text-[13px] text-neutral-700 transition hover:border-neutral-700 aria-expanded:border-accent aria-expanded:text-accent dark:border-neutral-800 dark:text-neutral-200 dark:hover:border-neutral-400";

export function SyncBadge({ compact = false }: { compact?: boolean }) {
  const { t } = useTranslation();
  const status = useSyncStatus();
  const tone =
    status === "synced"
      ? "ok"
      : status === "connecting" || status === "syncing"
        ? "warn"
        : status === "offline"
          ? "neutral"
          : "err";
  return (
    <Pill
      data-testid="sync-status"
      data-status={status}
      tone={tone}
      dot
      pulse={status === "connecting" || status === "syncing"}
      title={compact ? t(`sync.${status}`) : undefined}
      className={compact ? "px-1.5" : undefined}
    >
      <span className={compact ? "sr-only" : undefined}>{t(`sync.${status}`)}</span>
    </Pill>
  );
}

export function AppShell() {
  const { t, i18n } = useTranslation();
  // stays from a lost connection until everything is synced again: no flicker during reconnect attempts
  const outage = useSyncOutage();
  const session = useSession((s) => s.session);
  const logout = useSession((s) => s.logout);
  useIdleReturn();
  const levels = useLevels();
  const deviceAct = deviceAction(session?.device, session?.profile?.id);
  const { handle: core, workspaceId } = useCoreDoc();
  useTrackingReconcile(core, workspaceId);
  useSignalsLoader(workspaceId);
  const settings = useSettings(core);
  useApplySettings(settings);
  const circle = useCircle(core);
  // per-profile timers in shared spaces (spec §9.29); must be set before any tracking call
  setTrackingScope(circle.shared ? circle.me : null);
  const kid = circle.myKind === "kid";
  useEffect(() => {
    document.documentElement.dataset.kid = kid ? "true" : "false";
  }, [kid]);
  const { settled } = useDocState(core);
  const ctx = useViewCtx(core, workspaceId ?? "");
  const [params, setParams] = useSearchParams();
  // `?workspace=` on any navigation, not only at start: a task opened from another workspace's section of a
  // widget ("Show from") opens there (bug: it looked for the task in the current workspace)
  const wantedSpace = params.get("workspace");
  const selectWorkspace = useSession((s) => s.selectWorkspace);
  useEffect(() => {
    const w = wantedSpace && session?.workspaces?.find((x) => x.id === wantedSpace || x.name === wantedSpace);
    if (w && w.id !== workspaceId) selectWorkspace(w.id);
  }, [wantedSpace, session?.workspaces, workspaceId, selectWorkspace]);
  const taskId = params.get("task");
  const kiosk = params.get("kiosk") === "1";
  const navigate = useNavigate();
  const [assistant, setAssistant] = useState(false);
  // spec §9.18 / §9.29: no assistant for kid or viewer profiles; it is a `standard` feature
  const canAssist =
    !kid &&
    circle.myKind !== "viewer" &&
    session?.profile?.kind !== "viewer" &&
    atLeast(levels.settings, "standard") &&
    !!workspaceId;

  useEffect(() => {
    if (!canAssist) return;
    const onKey = (e: KeyboardEvent) => {
      if ((e.ctrlKey || e.metaKey) && e.key === "/") {
        e.preventDefault();
        setAssistant((v) => !v);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [canAssist]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (!core || !(e.ctrlKey || e.metaKey) || e.key.toLowerCase() !== "z") return;
      const target = e.target as HTMLElement | null;
      if (target && (target.tagName === "INPUT" || target.tagName === "TEXTAREA")) return; // native text undo wins
      e.preventDefault();
      if (e.shiftKey) redo(core.doc);
      else undo(core.doc);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [core]);
  void navigate;
  const layout = useLayout();
  const narrow = layout.mode === "narrow";
  const wide = sideBySide(layout);
  // the whole bar in one row only from 1800 px: below that a running timer and a long language (German) pushed it
  // past the edge, so the extras go into *More* as on medium screens (the panel still sits beside the page from 1200)
  const fullBar = layout.fullBar;
  const closePanel = useCallback(() => {
    const next = new URLSearchParams(params);
    next.delete("task");
    setParams(next);
  }, [params, setParams]);
  const navClass = ({ isActive }: { isActive: boolean }) =>
    clsx(
      navLink,
      !wide && "px-2",
      "touch:py-3",
      isActive && "bg-neutral-100 text-neutral-900 dark:bg-neutral-800 dark:text-neutral-100",
    );

  // the bar's extras: in the bar on very wide screens, in the *More* menu otherwise (same order either way); in the
  // menu each gets its name beside it, since there they are a list rather than a row of familiar controls
  const named = (label: string, node: ReactNode) =>
    fullBar ? (
      node
    ) : (
      <div className="flex w-full items-center justify-between gap-3">
        <span className="text-[13px] text-neutral-500">{label}</span>
        {node}
      </div>
    );
  // Settings: always in the bar, at the right (owner, 2026-10-24); the language is chosen there. On phones the tab
  // bar has it.
  const settingsLink = (
    <NavLink
      to="/settings"
      className={({ isActive }) =>
        clsx(
          barButton,
          // !px-0: barButton's own padding would otherwise win and squeeze the icon to a sliver
          "w-[30px] justify-center !px-0",
          isActive && "bg-neutral-100 text-neutral-900 dark:bg-neutral-800 dark:text-neutral-100",
        )
      }
      aria-label={t("nav.settings")}
      title={t("nav.settings")}
      data-testid="settings-link"
    >
      <Settings size={18} strokeWidth={1.75} className="shrink-0" aria-hidden />
    </NavLink>
  );
  const extras = (
    <>
      {(atLeast(levels.settings, "standard") || (session?.workspaces?.length ?? 0) > 1) &&
        named(t("layout.space"), <WorkspaceSwitch />)}
      {atLeast(levels.settings, "standard") &&
        (session?.workspaces?.length ?? 0) > 1 &&
        named(
          t("all.title"),
          <NavLink
            to="/all"
            className={clsx(barButton, fullBar && "w-[30px] justify-center !px-0")}
            aria-label={t("all.title")}
            title={t("all.title")}
            data-testid="all-link"
          >
            <Layers size={14} aria-hidden />
          </NavLink>,
        )}
      {canAssist && (
        <button
          type="button"
          className={barButton}
          aria-expanded={assistant}
          aria-keyshortcuts="Control+/"
          onClick={() => setAssistant((v) => !v)}
          data-testid="assistant-toggle"
        >
          {t("assistant.title")}
        </button>
      )}
      {/* on phones the navigation is hidden: the handbook stays in the More menu */}
      {narrow &&
        named(
          t("help.handbook"),
          <a
            href={helpUrl("", i18n.language)}
            target="_blank"
            rel="noopener"
            className={clsx(barButton, fullBar && "w-[30px] justify-center !px-0")}
            aria-label={t("help.handbook")}
            title={t("help.handbook")}
            data-testid="handbook-link"
          >
            <BookOpen size={14} aria-hidden />
          </a>,
        )}
      {session?.mode !== "solo-open" && session?.principal && (
        <span className="flex items-center gap-2">
          {session.mode === "circle" && (
            <span className="text-[13px] text-neutral-700 dark:text-neutral-200" data-testid="signed-in-as">
              {session.profile?.name ?? ""}
            </span>
          )}
          <button
            type="button"
            className={barButton}
            onClick={() =>
              deviceAct === "lend"
                ? void lendDevice()
                : deviceAct === "giveBack"
                  ? void giveBack()
                  : void logout().then(() => location.assign("/"))
            }
            data-testid="switch-profile"
            data-action={deviceAct}
          >
            {session.mode === "circle" ? t(`who.${deviceAct}`) : t("session.signOut")}
          </button>
        </span>
      )}
    </>
  );

  return (
    // the bars stay put; the page and the task panel each scroll inside their own column
    <div className="flex h-dvh flex-col overflow-hidden">
      <a
        href="#main"
        className="sr-only focus:not-sr-only focus:absolute focus:left-2 focus:top-2 focus:z-50 focus:rounded-sm focus:bg-[var(--surface)] focus:px-3 focus:py-2"
      >
        {t("a11y.skipToContent")}
      </a>
      {!kiosk && (
        <header
          className={clsx(
            "relative flex shrink-0 items-center border-b border-neutral-200 bg-[var(--ground)] text-sm dark:border-neutral-800",
            wide ? "gap-4 px-7 py-3.5" : "gap-2 px-3 py-2 mid:px-4",
            // an installed app on a phone draws under the status bar (viewport-fit=cover)
            narrow && "pt-[max(0.5rem,env(safe-area-inset-top))]",
            "short:py-1",
          )}
        >
          <NavLink
            to="/"
            className="flex items-center gap-2.5 text-neutral-900 dark:text-neutral-100"
            aria-label={t("app.name")}
          >
            <span className="grid h-7 w-7 place-items-center rounded-[7px] bg-accent text-[13px] font-bold tracking-[0.02em] text-white">
              Dr
            </span>
            {(fullBar || narrow) && <span className="font-semibold tracking-[-0.01em]">{t("app.name")}</span>}
          </NavLink>
          {!narrow && (
            <nav className={clsx("flex items-center", wide ? "gap-1" : "gap-0.5")} aria-label="primary">
              <DashboardsMenu core={core} />
              <NavLink to="/list" className={navClass}>
                {t("nav.list")}
              </NavLink>
              <NavLink to="/planner" className={navClass}>
                {t("nav.planner")}
              </NavLink>
              <NavLink to="/worklog" className={navClass}>
                {t("nav.worklog")}
              </NavLink>
              {/* where Settings used to be (owner, 2026-10-24); opens the handbook in a new tab */}
              <a
                href={helpUrl("", i18n.language)}
                target="_blank"
                rel="noopener"
                className={clsx(navLink, !wide && "px-2", "touch:py-3", "inline-flex items-center gap-1.5")}
                data-testid="handbook-link"
              >
                <BookOpen size={14} aria-hidden />
                {t("help.handbook")}
              </a>
            </nav>
          )}
          <div className={clsx("ml-auto flex items-center", fullBar ? "gap-2.5" : "gap-2")}>
            <SearchBox core={core} collapsible={!fullBar} />
            {fullBar && extras}
            {!kid && layout.barRoom && <TrackingStatus core={core} workspaceId={workspaceId} />}
            <SyncBadge compact={!fullBar} />
            {!narrow && settingsLink}
            {!fullBar && <MoreMenu>{extras}</MoreMenu>}
          </div>
        </header>
      )}
      {outage && (
        <div
          role="status"
          className="shrink-0 border-b border-neutral-200 bg-[var(--surface-2)] px-7 py-2 text-[13px] text-neutral-500 dark:border-neutral-800"
        >
          {t("sync.offlineBanner")}
        </div>
      )}
      <main id="main" className="flex min-h-0 flex-1">
        <div className="min-w-0 flex-1 overflow-y-auto" data-testid="page-scroll">
          <Outlet />
        </div>
        {taskId && !kiosk && ctx && settled && (
          // the same element in every layout: rotating or resizing keeps what is typed in it (§9.19)
          <PanelFrame place={wide ? "column" : narrow ? "sheet" : "drawer"} onClose={closePanel}>
            <TaskPanel key={taskId} taskId={taskId} ctx={ctx} onClose={closePanel} />
          </PanelFrame>
        )}
      </main>
      {!kiosk && !layout.barRoom && !kid && (
        <div className="flex shrink-0 justify-center border-t border-neutral-200 px-3 py-1.5 empty:hidden dark:border-neutral-800">
          <TrackingStatus core={core} workspaceId={workspaceId} />
        </div>
      )}
      {!kiosk && <TrayStrip core={core} compact={narrow || layout.short} />}
      {!kiosk && narrow && <TabBar core={core} />}
      <StarterAlarm core={core} />
      <Background
        core={core}
        workspaceId={workspaceId}
        settings={settings}
        kiosk={kiosk || circle.myKind !== "person"}
        me={circle.shared ? circle.me : null}
      />
      {canAssist && (
        <AssistantPanel
          workspaceId={workspaceId!}
          level={levels.settings}
          open={assistant}
          onClose={() => setAssistant(false)}
          full={narrow}
        />
      )}
      <Toasts />
    </div>
  );
}
